import { Component, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface FilterOption {
  id: string;
  label: string;
  checked?: boolean;
}

export interface FilterSection {
  id: string;
  title: string;
  type: 'checkbox' | 'radio' | 'search' | 'date';
  options?: FilterOption[];
  searchPlaceholder?: string;
  searchQuery?: string; // Local search term to filter options within this section
  isExpanded?: boolean;
  icon?: string; // Icon path for the filter section
}

export interface FilterConfig {
  sections: FilterSection[];
  activeFilterCount: number;
}

@Component({
  selector: 'app-asset-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asset-filter.component.html',
  styleUrl: './asset-filter.component.scss',
})
export class AssetFilterComponent {
  @Input() config!: FilterConfig;
  @Input() searchPlaceholder: string = 'Search for assets';
  @Input() isLoggedIn: boolean = false;
  /** When true, all interactions are blocked and the sidebar is visually locked. */
  @Input() disabled: boolean = false;
  @Output() filterChange = new EventEmitter<FilterConfig>();
  @Output() resetFilters = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();
  /** Emitted when a filter section accordion is expanded or collapsed. */
  @Output() sectionExpandChange = new EventEmitter<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  toggleSection(sectionId: string): void {
    if (this.disabled) return;
    const section = this.config.sections.find((s) => s.id === sectionId);
    if (!section) {
      return;
    }
    const wasExpanded = !!section.isExpanded;
    section.isExpanded = !section.isExpanded;
    // Dropping accordion local search when closing avoids stale text on reopen when nothing stayed selected.
    if (
      wasExpanded &&
      section.searchPlaceholder &&
      (section.type === 'checkbox' || section.type === 'radio')
    ) {
      section.searchQuery = '';
    }
    this.sectionExpandChange.emit();
    this.cdr.detectChanges();
  }

  onRadioClick(sectionId: string, optionId: string, event: Event, index: number): void {
    event.preventDefault();
    if (this.disabled) return;
    const section = this.config.sections.find(s => s.id === sectionId);
    if (section && section.options) {
      const clickedOption = section.options.find(o => o.id === optionId);
      if (!clickedOption) return;
      // If the clicked option is already selected, uncheck it (clear selection)
      if (clickedOption.checked) {
        section.options.forEach(opt => { opt.checked = false; });
      } else {
        // Otherwise select the clicked option and uncheck others
        section.options.forEach(opt => { opt.checked = opt.id === optionId; });
      }
      this.cdr.detectChanges();
      this.updateActiveFilterCount();
      this.filterChange.emit(this.config);
    }
  }

  /** Handle checkbox click so check and uncheck both work (fixes Last Updated At unable to uncheck). */
  onCheckboxClick(event: Event, sectionId: string, optionId: string): void {
    event.preventDefault();
    if (this.disabled) return;
    const section = this.config.sections.find(s => s.id === sectionId);
    if (section && section.options && section.type === 'checkbox') {
      const option = section.options.find(o => o.id === optionId);
      if (option) {
        option.checked = !option.checked;
        this.updateActiveFilterCount();
        this.filterChange.emit(this.config);
        this.cdr.detectChanges();
      }
    }
  }

  onOptionChange(sectionId: string, optionId: string, checked: boolean): void {
    if (this.disabled) return;
    const section = this.config.sections.find(s => s.id === sectionId);
    if (section && section.options) {
      // For checkboxes, just update the selected option
      if (section.type === 'checkbox') {
        const option = section.options.find(o => o.id === optionId);
        if (option) {
          option.checked = checked;
        }
      }
      // Radio buttons are handled by onRadioClick, so we skip them here
      this.updateActiveFilterCount();
      this.filterChange.emit(this.config);
      this.cdr.detectChanges();
    }
  }

  onReset(): void {
    if (this.disabled) return;
    this.config.sections.forEach(section => {
      if (section.options) {
        section.options.forEach(option => {
          option.checked = false;
        });
      }
      section.searchQuery = '';
      // Collapse all sections (dropdowns) when reset is clicked
      section.isExpanded = false;
    });
    this.updateActiveFilterCount();
    this.resetFilters.emit();
    this.filterChange.emit(this.config);
    this.cdr.detectChanges();
  }

  /**
   * Get options for a section, filtered by the section's search query when present.
   */
  getFilteredOptions(section: FilterSection): FilterOption[] {
    if (!section.options) return [];
    const query = (section.searchQuery || '').trim().toLowerCase();
    if (!query) return section.options;
    return section.options.filter(
      opt => (opt.label || '').toLowerCase().includes(query)
    );
  }

  /** True when the user typed inline section search text but nothing in the option list matches. */
  showsNoMatchingFilterOptions(section: FilterSection): boolean {
    if (section.type !== 'checkbox' && section.type !== 'radio') return false;
    const q = (section.searchQuery || '').trim();
    if (!q) return false;
    if (!section.options?.length) return false;
    return this.getFilteredOptions(section).length === 0;
  }

  private updateActiveFilterCount(): void {
    let count = 0;
    this.config.sections.forEach(section => {
      if (section.options) {
        count += section.options.filter(opt => opt.checked).length;
      }
    });
    this.config.activeFilterCount = count;
  }
}

